import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import ImavenLoadingScreen from '../Components/Helpers/loading-screen';
import axios from 'axios';
import { isTeamMemberAuthenticated, getTeamUser } from '../services/teamAuthService';

/**
 * AdminRoute Component
 * 
 * A protected route component that only allows access to users with admin privileges.
 * 
 * Access Logic:
 * - If NO team members exist: Auth0 user has full admin access (legacy behavior)
 * - If team members exist AND user is logged in as team member with 'admin' role: Access granted
 * - If team members exist AND user is NOT admin role: Access denied, redirect to home
 */
const AdminRoute = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently, isLoading } = useAuth0();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  
  // Check team member auth status
  const isMemberAuthenticated = isTeamMemberAuthenticated();
  const memberUser = getTeamUser();

  // Check if user has admin privileges
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (isAuthenticated && !isLoading) {
        try {
          const token = await getAccessTokenSilently();
          
          // First, check if company has team members
          const response = await axios.get(`${process.env.REACT_APP_SERVER}team/members`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          const members = response.data.members || [];
          
          if (members.length === 0) {
            // No team members yet - Auth0 user has full admin access (legacy behavior)
            setIsAdmin(true);
            setCheckingAdmin(false);
          } else {
            // Team members exist - check if current user is an admin member
            if (isMemberAuthenticated && memberUser) {
              // User is logged in as a team member
              // Check if their role is 'admin'
              setIsAdmin(memberUser.role === 'admin');
              setCheckingAdmin(false);
            } else {
              // Team members exist but user hasn't done member login
              // They'll be redirected to member login by ProtectedRoute
              // For now, don't grant admin access
              setIsAdmin(false);
              setCheckingAdmin(false);
            }
          }
        } catch (error) {
          console.error('Failed to check admin status:', error);
          setIsAdmin(false);
          setCheckingAdmin(false);
        }
      }
    };

    checkAdminStatus();
  }, [isAuthenticated, isLoading, getAccessTokenSilently, isMemberAuthenticated, memberUser]);

  // Redirect non-admin users
  useEffect(() => {
    if (isAuthenticated && !checkingAdmin && !isAdmin) {
      navigate('/');
    }
  }, [isAuthenticated, checkingAdmin, isAdmin, navigate]);

  // Show loading screen while checking authentication or admin status
  if (isLoading || (isAuthenticated && checkingAdmin)) {
    return <ImavenLoadingScreen />;
  }

  // If user is authenticated and is an admin, show the protected content
  if (isAuthenticated && isAdmin) {
    return children;
  }

  // During redirect, return loading screen
  return <ImavenLoadingScreen />;
};

export default AdminRoute;
